import { useState, useEffect } from "react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api } from "@/lib/api";
import type { BillingData } from "@/lib/types";

// Stripe checkout/portal are real server-side redirects to an external
// stripe.com URL — fetch() can't navigate the browser to a cross-origin
// redirect target, so these submit a real <form> POST (matching how the
// pre-React Jinja billing page worked) instead of an XHR/fetch call.
async function postFormTo(action: string, fields: Record<string, string>) {
  const csrfRes = await fetch("/api/v1/csrf-token", { credentials: "include" });
  const csrfData = (await csrfRes.json()) as { token?: string; csrf_token?: string };
  const csrfToken = csrfData.token ?? csrfData.csrf_token ?? "";

  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  for (const [name, value] of Object.entries({ ...fields, csrf_token: csrfToken })) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  slate: "#7C7590",
  white: "#FFFFFF",
  border: "#E8E4DC",
  navy: "#1A3552",
} as const;

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 12,
  padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: T.slate,
};

// ── Main component ─────────────────────────────────────────────────────────────
export function RecruiterBilling() {
  const [data, setData] = useState<BillingData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<BillingData>("/recruiter/billing")
      .then(setData)
      .catch((err: Error) => setError(err.message ?? "Failed to load billing"));
  }, []);

  if (error) {
    return (
      <RecruiterShell activeNav="billing">
        <div style={{ padding: 32, color: "#B91C1C", fontSize: 14 }}>
          Error: {error}
        </div>
      </RecruiterShell>
    );
  }

  if (!data) {
    return (
      <RecruiterShell activeNav="billing">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            fontSize: 14,
            color: T.slate,
          }}
        >
          Loading…
        </div>
      </RecruiterShell>
    );
  }

  const licenceCount = data.licence_count ?? 30;
  const usedThisMonth = data.used_this_month ?? 0;

  return (
    <RecruiterShell activeNav="billing">
      {/* ── Page header ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.2,
          }}
        >
          Billing
        </h1>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 13.5,
            color: T.slate,
          }}
        >
          Manage your plan, licences, and payment details.
        </p>
      </div>

      {/* ── "CURRENT PLAN" label ─────────────────────────────────────── */}
      <p style={{ ...monoLabel, marginBottom: 10 }}>CURRENT PLAN</p>

      {/* ── Plan card ────────────────────────────────────────────────── */}
      <div
        style={{
          ...cardStyle,
          position: "relative",
          marginBottom: 16,
        }}
      >
        {/* Plan name + badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: T.ink,
            }}
          >
            {data.plan}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.10em",
              textTransform: "uppercase" as const,
              color: T.slate,
              background: "#F3F4F6",
              border: "1px solid #E5E7EB",
              borderRadius: 999,
              padding: "3px 10px",
            }}
          >
            {data.plan_tag}
          </span>
        </div>

        {/* Licence total */}
        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13.5,
            color: T.slate,
          }}
        >
          {licenceCount} candidate licences
        </p>

        {/* Divider */}
        <div
          style={{
            borderTop: `1px solid ${T.border}`,
            marginBottom: 16,
          }}
        />

        {/* Usage section */}
        <p style={{ ...monoLabel, fontSize: 9, marginBottom: 10 }}>
          USED THIS MONTH
        </p>

        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span
            style={{
              fontSize: 48,
              fontWeight: 700,
              lineHeight: 1,
              color: T.ink,
            }}
          >
            {usedThisMonth}
          </span>
          <span
            style={{
              fontSize: 13.5,
              color: T.slate,
            }}
          >
            / {licenceCount} licences
          </span>
        </div>
      </div>

      {/* ── Manage subscription (existing Stripe customer) ──────────────── */}
      {data.stripe_configured && data.has_subscription && (
        <div style={{ ...cardStyle, marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: T.ink }}>
            Manage subscription
          </h2>
          <p style={{ margin: "0 0 20px", fontSize: 13.5, color: T.slate, lineHeight: 1.5 }}>
            Update your payment method, view invoices, or cancel your plan.
          </p>
          <button
            onClick={() => postFormTo("/coach/recruiter/billing/portal", {})}
            style={{
              display: "block", width: "100%", padding: "11px 20px",
              background: T.ink, color: T.white, border: "none", borderRadius: 999,
              fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "center" as const,
              transition: "opacity 0.12s",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "0.88"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = "1"; }}
          >
            Open billing portal
          </button>
        </div>
      )}

      {/* ── Upgrade plan card ─────────────────────────────────────────── */}
      <div style={{ ...cardStyle, marginBottom: 24 }}>
        <h2
          style={{
            margin: "0 0 8px",
            fontSize: 16,
            fontWeight: 700,
            color: T.ink,
          }}
        >
          {data.has_subscription ? "Change plan" : "Upgrade plan"}
        </h2>

        {data.stripe_configured ? (
          <>
            <p style={{ margin: "0 0 20px", fontSize: 13.5, color: T.slate, lineHeight: 1.5 }}>
              Choose a plan to unlock more candidate licences.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {data.plans.map((p) => (
                <div
                  key={p.id}
                  style={{
                    border: `1px solid ${T.border}`,
                    borderRadius: 10,
                    padding: 18,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{p.name}</span>
                    <span style={{ fontSize: 13.5, color: T.slate }}>{p.price}/{p.period}</span>
                  </div>
                  <ul style={{ margin: "0 0 14px", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                    {p.features.map((f) => (
                      <li key={f} style={{ fontSize: 12.5, color: T.slate }}>• {f}</li>
                    ))}
                  </ul>
                  <button
                    onClick={() => postFormTo("/coach/recruiter/billing/checkout", { plan_id: p.id })}
                    disabled={data.plan === p.id}
                    style={{
                      display: "block", width: "100%", padding: "10px 20px",
                      background: data.plan === p.id ? "#E8E4DC" : T.accent,
                      color: data.plan === p.id ? T.slate : T.white,
                      border: "none", borderRadius: 999, fontSize: 13.5, fontWeight: 600,
                      cursor: data.plan === p.id ? "default" : "pointer", textAlign: "center" as const,
                    }}
                  >
                    {data.plan === p.id ? "Current plan" : `Subscribe to ${p.name}`}
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <p
              style={{
                margin: "0 0 20px",
                fontSize: 13.5,
                color: T.slate,
                lineHeight: 1.5,
              }}
            >
              Get in touch to upgrade your plan or discuss custom pricing.
            </p>
            <button
              onClick={() => {
                window.location.href = "mailto:hello@executiveinterview.coach";
              }}
              style={{
                display: "block",
                width: "100%",
                padding: "11px 20px",
                background: T.accent,
                color: T.white,
                border: "none",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "center" as const,
                transition: "opacity 0.12s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = "0.88";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.opacity = "1";
              }}
            >
              Contact us to upgrade
            </button>
          </>
        )}
      </div>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <p
        style={{
          margin: 0,
          fontSize: 12.5,
          color: T.slate,
          lineHeight: 1.6,
        }}
      >
        Billing is managed securely by Stripe. Contact{" "}
        <a
          href="mailto:hello@executiveinterview.coach"
          style={{
            color: T.accent,
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          support
        </a>{" "}
        for invoices or cancellations.
      </p>
    </RecruiterShell>
  );
}
